<template>
  <dara-invisible>
    <dara-row class="mb--md d-flex justify-content-center justify-content--space-between align-items-center">
      <dara-col :xs="24" :span="showRangeDate ? 12 : 24" class="mb--sm mt--xl">
        <dara-header size="md">
          <dara-invisible v-if="!label">
            {{ $t('user-ranking') }}
          </dara-invisible>
          <dara-invisible v-else>
            {{ label }}
          </dara-invisible>
        </dara-header>
      </dara-col>
      <dara-col v-if="showRangeDate" :xs="24" :span="13" class="mb--sm mt--xl d-flex justify-content-center">
        <dara-invisible class="ml--lg d-flex justify-content-center align-items-center">
          <dara-p class="mr--md mb--0 mt--md">{{ $t('from') }}</dara-p>
          <dara-input
            v-model="startDate"
            type="date"
            :name="$t('begin-date')"
            rules=""
            @change="fetchUsers()" />
        </dara-invisible>
        <dara-invisible class="ml--lg d-flex justify-content-center align-items-center">
          <dara-p class="mr--md mb--0 mt--md ">{{ $t('to') }}</dara-p>
          <dara-input
            v-model="endDate"
            type="date"
            :name="$t('end-date')"
            rules=""
            @change="fetchUsers()" />
        </dara-invisible>

        <div class="cursor--pointer ml--lg gray-squared-button d-flex justify-content-center align-items-center" name="download" @click="downloadCSV">
          <el-tooltip effect="dark" :content="$t('download-csv')" placement="top">
            <i class="el-icon-download" />
          </el-tooltip>
        </div>
      </dara-col>
    </dara-row>

    <dara-row class="mb--lg d-flex justify-content-center justify-content--space-between">
      <dara-col class="gray-card table-border">
        <div v-if="data && data.length > 0">
          <dara-user-ranking-header @orderBy="fetchUsers($event)" />
          <dara-invisible v-for="item in data.slice(0, pagination.limit)" :key="item.id">
            <hr class="line">
            <dara-user-ranking-item v-if="item" ref="bestWriterIndex" class="mt--lg" :can-follow="false" :user="item" />
          </dara-invisible>
        </div>
        <div v-if="data && data.length == 0">
          <dara-item-table-layout>
            <template #empty class="mt--md">
              <dara-header size="sm" class="mt--lg color--info">
                {{ $t('empty-users-table') }}
              </dara-header>
            </template>
          </dara-item-table-layout>
        </div>

        <div v-if="data == null">
          <dara-user-ranking-header @orderBy="fetchUsers($event)" />
          <dara-invisible v-for="item in users" :key="item.id">
            <hr class="line">
            <dara-user-ranking-item v-if="item" ref="bestWriterIndex" class="mt--lg" :can-follow="false" :user="item" />
          </dara-invisible>
        </div>
        <hr v-if="pagination.total > pagination.limit" class="line">
        <dara-pagination
          class="mt--xl"
          :background="true"
          :small="true"
          :page.sync="pagination.page"
          :total="pagination.total"
          :page-size="pagination.limit"
          @current-change="fetchUsers()" />
      </dara-col>
    </dara-row>
  </dara-invisible>
</template>

<script>
import { toastError } from '@/services/toastService';
import { translate } from '@/services/translate.service';
import { getMonthRange } from '@/services/timeService';

import { fetchRanking } from '@/modules/user/user.service';

import DaraUserRankingHeader from '@/modules/user/components/UserRankingTableHeader.vue';
import DaraUserRankingItem from '@/modules/user/components/UserRankingTableItem.vue';
import DaraItemTableLayout from '@/layouts/ItemTableLayout.vue';

export default {
  name: 'DaraRankingTable',
  components: {
    DaraUserRankingHeader,
    DaraUserRankingItem,
    DaraItemTableLayout,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    showRangeDate: {
      type: Boolean,
      default: true,
    },
    data: {
      type: Array,
      default: null,
    },
    dataPagination: {
      type: Object,
      default: null,
    },
    size: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      users: null,
      allUsers: null,

      pagination: this.dataPagination ? this.dataPagination : { page: 1, limit: this.size },

      by: null,
      order: null,

      date: new Date(),
      startDate: '',
      endDate: '',
    };
  },
  created() {
    const { start, end } = getMonthRange();
    this.startDate = start;
    this.endDate = end;
  },
  mounted() {
    if (this.data == null) {
      this.fetchUsers();
    }
  },
  methods: {
    fetchUsers(params = { by: null, order: null }) {
      if (this.startDate >= this.endDate) {
        toastError(translate('error-start-date-greater-then-end-date'));
        return;
      }

      this.by = params.by == null ? this.by : params.by;
      this.order = params.order == null ? this.order : params.order;

      const payload = {
        by: this.by,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        ...this.pagination,
      };

      if (this.data) {
        this.$emit('fetch', payload);
      } else {
        fetchRanking(payload)
          .then(data => {
            this.users = data.users;
            this.pagination.total = parseInt(data.total_users);
          })
          .catch(() => {
            toastError(`${translate('error-getting-users')}`);
          });
      }
    },
    async downloadCSV() {
      if (this.startDate >= this.endDate) {
        toastError(translate('error-start-date-greater-then-end-date'));
        return;
      }

      const payload = {
        by: this.by,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        page: 1,
        limit: 9999999999,
      };

      await fetchRanking(payload)
        .then(data => {
          this.allUsers = data.users;
        })
        .catch(() => {
          toastError(`${translate('error-getting-users')}`);
        });

      const users = [[translate('user-name'), translate('total-rating'), translate('created-articles'), translate('readed-articles'), translate('new-followers')]];
      this.allUsers.map(user =>
        users.push([user.name,
          user.total_rating,
          user.created_articles,
          user.readed_articles,
          user.followers]));

      const csvContent = 'data:text/csv;charset=utf-8,'.concat(users.map(e => e.join(',')).join('\n'));
      const encodedUri = encodeURI(csvContent);
      const donwload = document.createElement('a');
      donwload.setAttribute('href', encodedUri);
      donwload.setAttribute('download', 'users-ranking.csv');
      document.body.appendChild(donwload);
      donwload.click();
    },
  },
};
</script>

<style scoped>
  .gray-squared-button {
    height: 2.6rem;
    width: 2.6rem;
    border-radius: 5px;
    color: rgb(161, 161, 161);
    background-color: #f4f4f5 !important;
  }
  .gray-squared-button:hover {
    background-color: #f0f0f0 !important;
  }
  .gray-card {
    border-right: 10px solid transparent;
    border-left: 10px solid transparent;
    border-top: 20px solid transparent;
    border-bottom : 20px solid transparent;
    border-radius: 5px;
    margin: 0rem 0.6rem 0rem 0.6rem;
    background-color: #f4f4f5 !important;
  }
  .line {
    color: rgb(158, 158, 158) !important;
    width: 100%;
  }
  .table-border {
    border-right: 20px solid transparent !important;
    border-left: 20px solid transparent !important;
    border-top: 30px solid transparent !important;
    border-bottom : 30px solid transparent !important;
  }
</style>
